// iMIS cart payloads are large, weakly-typed `$type`-tagged objects; `any` is deliberate.
import iMISFetch from "@/utils/iMISFetch";

interface CartResult {
  success: boolean;
  message?: string;
}

/** RFC4122-ish GUID for a new order line id. */
function generateGuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Finds the member's ship address (needed for both new-cart creation and line adding). */
async function getShipAddress(id: string): Promise<any> {
  const party = await iMISFetch<any>("party", { record: id });
  const addrs = (party.data as any).Addresses.$values as any[];
  for (const addr of addrs) {
    const prefs = addr.CommunicationPreferences.$values as any[];
    for (const pref of prefs) {
      if (pref.Reason === "ship") return addr;
    }
  }
  return null;
}

/**
 * Adds a product to the member's cart (no coupon). Reuses an existing open cart
 * or creates one, then appends an order line and PUTs it back.
 *
 * @param id             selectedPartyId — the cart owner.
 * @param loggedInUserId the authenticated actor (cart CreatedBy/UpdatedBy).
 * @param productCode    the product to add.
 */
export async function addProductToCart(
  id: string,
  loggedInUserId: string | null,
  productCode: string
): Promise<CartResult> {
  try {
    const itemRes = await iMISFetch<any>("Item?ItemCode=" + productCode, {});
    if (!itemRes.success || itemRes.length === 0) {
      return { success: false, message: `Product ${productCode} not found` };
    }
    const itemData = (itemRes.data as any[])[0];

    const address = await getShipAddress(id);

    const cartRes = await iMISFetch<any>("Cart?UserId=" + id + "&CreatedBy=" + loggedInUserId, {});
    let cartId: string | null = null;

    if (cartRes.success && cartRes.length > 0) {
      cartId = (cartRes.data as any[])[0].CartId;
    } else {
      const now = new Date().toISOString();
      const cartData = {
        $type: "Asi.Soa.Commerce.DataContracts.CartData, Asi.Contracts",
        UserId: id,
        ComboOrder: {
          $type: "Asi.Soa.Commerce.DataContracts.ComboOrderData, Asi.Contracts",
          Currency: { $type: "Asi.Soa.Core.DataContracts.CurrencyData, Asi.Contracts", CurrencyCode: "USD", DecimalPositions: 2 },
          Order: {
            $type: "Asi.Soa.Commerce.DataContracts.OrderData, Asi.Contracts",
            Lines: {
              $type: "Asi.Soa.Commerce.DataContracts.OrderLineDataCollection, Asi.Contracts",
              $values: [],
            },
            Delivery: {
              $type: "Asi.Soa.Commerce.DataContracts.DeliveryDataCollection, Asi.Contracts",
              $values: [
                {
                  $type: "Asi.Soa.Commerce.DataContracts.DeliveryData, Asi.Contracts",
                  AddressId: address.Address.AddressId,
                  Address: address,
                  CustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
                  DeliveryMethod: {
                    $type: "Asi.Soa.Commerce.DataContracts.DeliveryMethodData, Asi.Contracts",
                    DeliveryMethodId: "Fedex",
                  },
                },
              ],
            },
            SoldToCustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
            BillToCustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
            OriginatorCustomerParty: { $type: "Asi.Soa.Commerce.DataContracts.CustomerPartyData, Asi.Contracts", PartyId: id },
            SourceCode: "",
          },
          Invoices: { $type: "Asi.Soa.Commerce.DataContracts.InvoiceSummaryDataCollection, Asi.Contracts", $values: [] },
          Payments: { $type: "Asi.Soa.Commerce.DataContracts.RemittanceDataCollection, Asi.Contracts", $values: [] },
          OrderTypeCode: "PF",
        },
        UpdateInformation: {
          $type: "Asi.Soa.Core.DataContracts.EntityUpdateInformationData, Asi.Contracts",
          CreatedBy: loggedInUserId,
          CreatedOn: now,
          UpdatedBy: loggedInUserId,
          UpdatedOn: now,
        },
      };
      const newCart = await iMISFetch<any>("Cart", { method: "POST", data: JSON.stringify(cartData) });
      if (!newCart.success) return { success: false, message: "Failed to create cart" };
      cartId = (newCart.data as any).CartId;
    }

    const fullCart = await iMISFetch<any>("Cart", { method: "GET", record: cartId! });
    if (!fullCart.success) return { success: false, message: "Failed to retrieve cart" };

    const cart = fullCart.data as any;
    if (!cart.ComboOrder.Order.Lines.$values) {
      cart.ComboOrder.Order.Lines.$values = [];
    }

    const existingLines = cart.ComboOrder.Order.Lines.$values as any[];
    const nextLineNum = existingLines.length + 1;

    existingLines.push({
      $type: "Asi.Soa.Commerce.DataContracts.OrderLineData, Asi.Contracts",
      OrderLineId: generateGuid(),
      Item: {
        $type: "Asi.Soa.Commerce.DataContracts.ItemSummaryData, Asi.Contracts",
        ItemClass: itemData.ItemClass,
        ItemCode: itemData.ItemCode,
        ItemId: itemData.ItemId,
        Name: itemData.Name,
      },
      LineNumber: nextLineNum,
      Note: "",
      CanCombine: true,
      SourceCode: "",
      IsUnitPriceOverridden: false,
      QuantityOrdered: {
        $type: "System.Nullable`1[[Asi.Soa.Commerce.DataContracts.QuantityData, Asi.Contracts]], mscorlib",
        Amount: 1,
      },
      QuantityBackordered: {
        $type: "System.Nullable`1[[Asi.Soa.Commerce.DataContracts.QuantityData, Asi.Contracts]], mscorlib",
      },
    });

    const pricedOrder = await getUpdateOrderPricing(cart.ComboOrder.Order);
    if (!pricedOrder) return { success: false, message: "Failed to update cart pricing" };
    cart.ComboOrder.Order = pricedOrder;

    const updated = await iMISFetch<any>("Cart", {
      method: "PUT",
      record: cartId!,
      data: JSON.stringify(cart),
    });
    if (!updated.success) return { success: false, message: "Failed to update cart" };
    return { success: true };
  } catch (e) {
    console.error(e);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/** Runs the UpdatePricing operation so newly added lines get a real price. */
async function getUpdateOrderPricing(order: any): Promise<any> {
  const RESTData = {
    $type: "Asi.Soa.Commerce.DataContracts.OrderPriceUpdateRequest, Asi.Contracts",
    EntityTypeName: "Order",
    OperationName: "UpdatePricing",
    Order: order,
  };
  const res = await iMISFetch<any>("Order/_Execute", { method: "POST", data: JSON.stringify(RESTData) });
  return res.data;
}
